import type { SourceStatus } from '@panelvault/shared';
import {
  ArrowDown,
  ArrowUp,
  CheckCircle2,
  ExternalLink,
  KeyRound,
  ScanBarcode,
  XCircle,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Checkbox, Select } from '../../components/ui/controls';
import { Input } from '../../components/ui/input';
import { Panel } from '../../components/ui/panel';
import { Skeleton } from '../../components/ui/skeleton';
import { ApiError } from '../../lib/api';
import {
  useClearSourceCache,
  useLookup,
  useSources,
  useUpdateSource,
  useVerifySource,
} from './queries';

const CAPABILITY_KEYS = ['barcode', 'isbn', 'search', 'seriesIssues'] as const;

/** Cache lifetimes offered in the settings, in days. */
const TTL_DAYS = [1, 7, 30, 90, 365] as const;

function SourceRow({
  source,
  isFirst,
  isLast,
  onMove,
}: {
  source: SourceStatus;
  isFirst: boolean;
  isLast: boolean;
  onMove: (direction: -1 | 1) => void;
}) {
  const { t } = useTranslation();
  const update = useUpdateSource();
  const verify = useVerifySource();
  const [credential, setCredential] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function saveCredential() {
    setError(null);
    try {
      await update.mutateAsync({ source: source.source, update: { credential } });
      setCredential('');
      await verify.mutateAsync(source.source);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : t('form.saveFailed'));
    }
  }

  return (
    <div className="flex flex-col gap-3 px-5 py-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-lg text-foreground">{source.label}</h3>
            {source.ready ? (
              <Badge variant="success">{t('sources.ready')}</Badge>
            ) : (
              <Badge variant="outline">
                {t('sources.needsKey', { label: source.credentialLabel })}
              </Badge>
            )}
          </div>
          <p className="mt-1 max-w-2xl text-sm text-muted">{source.description}</p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {CAPABILITY_KEYS.map((key) => (
              <Badge key={key} variant={source.capabilities[key] ? 'accent' : 'outline'}>
                {t(`sources.capability.${key}`)}
              </Badge>
            ))}
          </div>
          {source.attribution ? (
            <p className="mt-2 text-xs text-muted">{source.attribution}</p>
          ) : null}
        </div>

        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            disabled={isFirst || update.isPending}
            onClick={() => onMove(-1)}
            aria-label={t('sources.moveUp')}
          >
            <ArrowUp className="size-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            disabled={isLast || update.isPending}
            onClick={() => onMove(1)}
            aria-label={t('sources.moveDown')}
          >
            <ArrowDown className="size-4" />
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
        <Checkbox
          label={t('sources.enabled')}
          checked={source.enabled}
          disabled={update.isPending}
          onChange={(event) =>
            void update.mutateAsync({
              source: source.source,
              update: { enabled: event.target.checked },
            })
          }
        />

        <label className="inline-flex items-center gap-2 text-sm text-muted">
          {t('sources.cacheTtl')}
          <Select
            className="h-9 w-auto"
            value={String(Math.round(source.cacheTtlHours / 24))}
            disabled={update.isPending}
            onChange={(event) =>
              void update.mutateAsync({
                source: source.source,
                update: { cacheTtlHours: Number(event.target.value) * 24 },
              })
            }
          >
            {TTL_DAYS.map((days) => (
              <option key={days} value={days}>
                {t('sources.days', { count: days })}
              </option>
            ))}
          </Select>
        </label>

        <a
          href={source.docsUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-sm text-muted hover:text-accent"
        >
          <ExternalLink className="size-3.5" />
          {t('sources.docs')}
        </a>

        {source.lastCheckedAt ? (
          <span className="inline-flex items-center gap-1 text-xs text-muted">
            {source.lastError ? (
              <XCircle className="size-3.5 text-danger" />
            ) : (
              <CheckCircle2 className="size-3.5 text-success" />
            )}
            {source.lastError ?? t('sources.checkOk')}
          </span>
        ) : null}
      </div>

      {source.options.map((option) => (
        <div
          key={option.key}
          className="flex flex-col gap-1.5 rounded-lg border bg-surface-raised p-3"
        >
          <Checkbox
            label={option.label}
            checked={option.value}
            disabled={update.isPending}
            onChange={(event) =>
              void update.mutateAsync({
                source: source.source,
                update: { options: { [option.key]: event.target.checked } },
              })
            }
          />
          <p className="text-xs text-muted">{option.description}</p>
        </div>
      ))}

      {source.requiresCredentials ? (
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-60 flex-1">
            <KeyRound className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted" />
            <Input
              type="password"
              autoComplete="off"
              className="pl-9"
              value={credential}
              placeholder={
                source.hasCredentials
                  ? t('sources.credentialStored', { label: source.credentialLabel })
                  : t('sources.credentialPlaceholder', { label: source.credentialLabel })
              }
              onChange={(event) => setCredential(event.target.value)}
            />
          </div>
          <Button
            size="sm"
            disabled={credential.trim() === '' || update.isPending || verify.isPending}
            onClick={() => void saveCredential()}
          >
            {t('sources.saveAndTest')}
          </Button>
          {source.hasCredentials ? (
            <>
              <Button
                size="sm"
                variant="surface"
                disabled={verify.isPending}
                onClick={() => void verify.mutateAsync(source.source)}
              >
                {verify.isPending ? t('sources.testing') : t('sources.test')}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                disabled={update.isPending}
                onClick={() =>
                  void update.mutateAsync({ source: source.source, update: { credential: '' } })
                }
              >
                {t('sources.removeCredential')}
              </Button>
            </>
          ) : null}
        </div>
      ) : null}

      {error ? (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/** Small tester so the adapters can be checked before the scanner exists. */
function LookupTester() {
  const { t } = useTranslation();
  const lookup = useLookup();
  const [code, setCode] = useState('');

  const result = lookup.data;

  return (
    <div className="flex flex-col gap-3 border-t px-5 py-4">
      <div>
        <h3 className="text-lg text-foreground">{t('sources.testerTitle')}</h3>
        <p className="mt-1 text-sm text-muted">{t('sources.testerBody')}</p>
      </div>

      <form
        className="flex flex-wrap gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          if (code.trim()) lookup.mutate({ code: code.trim() });
        }}
      >
        <Input
          className="min-w-60 flex-1"
          inputMode="numeric"
          placeholder="9783551732347"
          value={code}
          onChange={(event) => setCode(event.target.value)}
        />
        <Button type="submit" variant="surface" disabled={lookup.isPending || code.trim() === ''}>
          <ScanBarcode className="size-4" />
          {lookup.isPending ? t('sources.testing') : t('sources.lookup')}
        </Button>
      </form>

      {lookup.error ? (
        <p className="text-sm text-danger">
          {lookup.error instanceof ApiError ? lookup.error.message : t('form.saveFailed')}
        </p>
      ) : null}

      {result ? (
        <div className="flex flex-col gap-2 rounded-lg border bg-surface-raised p-3 text-sm">
          <p className="text-muted">
            {t('sources.readAs', {
              kind: result.barcode.kind,
              code: result.barcode.normalized,
            })}
            {result.barcode.addon ? ` + ${result.barcode.addon}` : ''}
            {result.barcode.checkDigitValid ? '' : ` · ${t('sources.badCheckDigit')}`}
          </p>

          {result.local ? (
            <p className="text-foreground">
              {t('sources.localHit', {
                title: result.local.seriesTitle,
                number: result.local.number,
                count: result.local.ownedQuantity,
              })}
            </p>
          ) : null}

          {result.matches.map((match) => (
            <p key={`${match.issue.source}-${match.issue.externalId}`} className="text-foreground">
              <Badge variant="accent" className="mr-2">
                {match.issue.source}
              </Badge>
              {match.issue.seriesName} #{match.issue.number}
              {match.issue.publisherName ? (
                <span className="text-muted"> · {match.issue.publisherName}</span>
              ) : null}
            </p>
          ))}

          <div className="flex flex-wrap gap-1.5">
            {result.searched.map((note) => (
              <Badge
                key={note.source}
                variant={
                  note.status === 'hit' || note.status === 'cached'
                    ? 'success'
                    : note.status === 'error'
                      ? 'danger'
                      : 'outline'
                }
                title={note.message ?? undefined}
              >
                {note.source}: {t(`sources.status.${note.status}`)}
              </Badge>
            ))}
          </div>

          {!result.local && result.matches.length === 0 ? (
            <p className="text-muted">{t('sources.noMatch')}</p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export function SourcesPanel() {
  const { t } = useTranslation();
  const sources = useSources();
  const update = useUpdateSource();
  const clearCache = useClearSourceCache();

  const list = sources.data ?? [];

  /** Swaps the order values of two neighbours. */
  async function move(index: number, direction: -1 | 1) {
    const current = list[index];
    const neighbour = list[index + direction];
    if (!current || !neighbour) return;

    await update.mutateAsync({ source: current.source, update: { order: neighbour.order } });
    await update.mutateAsync({ source: neighbour.source, update: { order: current.order } });
  }

  return (
    <Panel className="overflow-hidden">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b px-5 py-4">
        <div>
          <h2 className="text-xl text-foreground">{t('settings.sourcesTitle')}</h2>
          <p className="mt-1 max-w-2xl text-sm text-muted">{t('sources.intro')}</p>
        </div>
        <Button
          size="sm"
          variant="ghost"
          disabled={clearCache.isPending}
          onClick={() => void clearCache.mutateAsync()}
        >
          {clearCache.data
            ? t('sources.cacheCleared', { count: clearCache.data.removed })
            : t('sources.clearCache')}
        </Button>
      </div>

      {sources.isPending ? (
        <div className="p-5">
          <Skeleton className="h-40" />
        </div>
      ) : (
        <div className="divide-y">
          {list.map((source, index) => (
            <SourceRow
              key={source.source}
              source={source}
              isFirst={index === 0}
              isLast={index === list.length - 1}
              onMove={(direction) => void move(index, direction)}
            />
          ))}
        </div>
      )}

      <LookupTester />
    </Panel>
  );
}
